import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { HelpCircleDarkIcon, LogoutDarkIcon, SettingsRoundedIcon } from "../../../assets";
import { OrganisationTypeConstants } from "../../../constants";
import { FileType } from "../../../models";
import { getAssetManagerDetails, GetCurrentUserResponse } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import {
  getDashboardRoute,
  getInvestmentDirectoryRoute,
  getOperationsCompaniesRoute,
  getOperationsDealWorkflowsRoute,
  getPipelineTrackerRoute,
  getProfileChangePasswordRoute,
  getProfileChangePersonalDetailsRoute,
  getSettingsRoute,
} from "../../../utils/routes";
import { ProfileMenuItemType } from "../ProfileMenuItem";

type HeaderTextLink = {
  route: string;
  label: string;
};

type HeaderTextDropdown = {
  label: string;
  links: ProfileMenuItemType[];
};

export type HeaderTextElement = HeaderTextLink | HeaderTextDropdown;

interface UseSeedNavigationReturnData {
  showProfileMenu: boolean;
  setShowProfileMenu: (flag: boolean) => void;
  showHelpCenter: boolean;
  setShowHelpCenter: (flag: boolean) => void;
  currentUserData: GetCurrentUserResponse | undefined;
  optionList: ProfileMenuItemType[];
  onOptionSelect: (option: ProfileMenuItemType) => void;
  headerTextElements: HeaderTextElement[];
  headerIcons: {
    onClick?: () => void;
    route?: string;
    key: string;
    icon: JSX.Element;
  }[];
  isLoading: boolean;
  logoUrl: string | null | undefined;
}

const headerTextElements: HeaderTextElement[] = [
  {
    route: getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER),
    label: "Dashboard",
  },
  {
    label: "Portfolio",
    links: [
      {
        id: 1,
        value: "Investment directory",
        route: getInvestmentDirectoryRoute(OrganisationTypeConstants.ASSET_MANAGER),
      },
      {
        id: 2,
        value: "Pipeline",
        route: getPipelineTrackerRoute(OrganisationTypeConstants.ASSET_MANAGER),
      },
    ],
  },
  {
    label: "Operations",
    links: [
      {
        id: 1,
        value: "CRM",
        route: getOperationsCompaniesRoute(OrganisationTypeConstants.ASSET_MANAGER),
      },
      {
        id: 2,
        value: "Workflows",
        route: getOperationsDealWorkflowsRoute(OrganisationTypeConstants.ASSET_MANAGER),
      },
    ],
  },
  // TODO - Enable when implemented
  // {
  //   route: getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER),
  //   label: "Projects",
  // },
];

export const useSeedNavigation = (): UseSeedNavigationReturnData => {
  const navigate = useNavigate();
  const { user, logoutUser, currentUserType, currentAssetManagerUuid } = useAuth();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const logoCacheKey = currentAssetManagerUuid ? `seed-navigation-logo:${currentAssetManagerUuid}` : undefined;
  const [logoUrl, setLogoUrl] = useState<string | null | undefined>(() => {
    if (!logoCacheKey) return undefined;
    return localStorage.getItem(logoCacheKey);
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!logoCacheKey) return false;
    return !localStorage.getItem(logoCacheKey);
  });

  const headerIcons = [
    // TODO - Enable when implemented
    // {
    //   route: getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER),
    //   key: "NatureScript",
    //   icon: <NatureScriptIcon className="NavigationIcon" />,
    // },
    // {
    //   route: getDashboardRoute(OrganisationTypeConstants.ASSET_MANAGER),
    //   key: "Notifications",
    //   icon: <NotificationsDarkIcon className="NavigationIcon" />,
    // },
    {
      onClick: () => setShowHelpCenter((prev) => !prev),
      key: "Help",
      icon: <HelpCircleDarkIcon className="HelpIcon" />,
    },
    {
      route: getSettingsRoute(OrganisationTypeConstants.ASSET_MANAGER),
      key: "Settings",
      icon: <SettingsRoundedIcon className="NavigationIcon" />,
    },
  ];

  const initialOptionList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Change personal details",
      route: getProfileChangePersonalDetailsRoute(currentUserType),
    },
    {
      id: 2,
      value: "Change password",
      route: getProfileChangePasswordRoute(currentUserType),
    },
    {
      id: 3,
      value: "Log out",
      icon: <LogoutDarkIcon />,
      action: () => logoutUser(),
    },
  ];

  const [optionList] = useState(initialOptionList);

  // eslint-disable-next-line unused-imports/no-unused-vars
  const onOptionSelect = (option: ProfileMenuItemType): void => {
    if (option.action) {
      option.action();
    }
    if (option.route) {
      navigate(option.route);
    }
    setShowProfileMenu(false);
  };

  useEffect(() => {
    if (!logoCacheKey) {
      setIsLoading(false);
      return;
    }

    (async () => {
      if (!currentAssetManagerUuid) {
        setIsLoading(false);
        return;
      }

      const assetManagerDetailsRes = await getAssetManagerDetails({ assetManagerUuid: currentAssetManagerUuid });
      if (assetManagerDetailsRes.status === Status.Success && assetManagerDetailsRes.data) {
        const nextLogoUrl =
          assetManagerDetailsRes.data?.files.find((f) => f.type === FileType.LargeLogo)?.file.url || null;
        setLogoUrl(nextLogoUrl);
        if (nextLogoUrl) {
          localStorage.setItem(logoCacheKey, nextLogoUrl);
        }
      }
      setIsLoading(false);
    })().catch(() => {
      setIsLoading(false);
    });
  }, [currentAssetManagerUuid, logoCacheKey]);

  return {
    showProfileMenu,
    setShowProfileMenu,
    showHelpCenter,
    setShowHelpCenter,
    currentUserData: user,
    optionList,
    onOptionSelect,
    headerIcons,
    headerTextElements,
    logoUrl,
    isLoading,
  };
};
